<template>
    <div class="app-container">
      <!-- 标题区域 -->
      <div class="header-banner">
        <div class="banner-content">
          <h1>智能医疗报告解读</h1>
          <p>基于先进的人工智能算法，快速理解医疗报告内容，提供专业解读和健康建议</p>
          <el-button type="primary" size="large" icon="el-icon-upload2" @click="startAnalysis">开始报告解读</el-button>
        </div>
      </div>
  
      <!-- 功能介绍区域 -->
      <el-card class="feature-card">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-s-opportunity"></i> AI解读优势</span>
        </div>
        <div class="feature-content">
          <el-row :gutter="20">
            <el-col :span="6" v-for="(feature, index) in features" :key="index">
              <div class="feature-item">
                <div class="feature-icon">
                  <i :class="feature.icon"></i>
                </div>
                <h3>{{ feature.title }}</h3>
                <p>{{ feature.description }}</p>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>
  
      <!-- 上传解读区域 -->
      <el-card class="upload-card" v-if="showUpload">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-upload"></i> 上传您的医疗报告</span>
          <el-button style="float: right" type="text" @click="showUpload = false">关闭</el-button>
        </div>
        <div class="upload-area">
          <el-tabs v-model="uploadType">
            <el-tab-pane label="上传文件" name="file">
              <el-upload
                class="upload-demo"
                drag
                action="#"
                :auto-upload="false"
                :on-change="handleFileChange"
                :limit="1"
                :file-list="fileList">
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">拖拽文件到此处，或<em>点击上传</em></div>
                <div class="el-upload__tip" slot="tip">支持上传PDF、JPG、PNG格式的报告文件，单个文件不超过20MB</div>
              </el-upload>
            </el-tab-pane>
            <el-tab-pane label="拍照上传" name="camera">
              <div class="camera-container">
                <i class="el-icon-camera-solid"></i>
                <p>点击启用摄像头拍摄报告</p>
                <el-button type="primary" plain>开启摄像头</el-button>
              </div>
            </el-tab-pane>
            <el-tab-pane label="从医院记录选择" name="record">
              <div class="record-container">
                <el-input placeholder="搜索您的检查报告" prefix-icon="el-icon-search" v-model="searchKeyword"></el-input>
                <el-table :data="recentReports" style="width: 100%; margin-top: 15px;">
                  <el-table-column prop="date" label="检查日期" width="180"></el-table-column>
                  <el-table-column prop="name" label="报告名称"></el-table-column>
                  <el-table-column prop="department" label="科室"></el-table-column>
                  <el-table-column label="操作" width="120">
                    <template slot-scope="scope">
                      <el-button @click="selectReport(scope.row)" type="text" size="small">选择</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-tab-pane>
          </el-tabs>
          <div class="upload-action">
            <el-button type="primary" @click="analyzeReport" :loading="analyzing">开始解读</el-button>
            <el-button @click="resetUpload">重置</el-button>
          </div>
        </div>
      </el-card>
  
      <!-- 解读效果展示区域 -->
      <el-card class="demo-card">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-data-analysis"></i> 解读效果示例</span>
        </div>
        <div class="demo-content">
          <el-tabs v-model="demoTab">
            <el-tab-pane label="肺部CT报告" name="lung">
              <el-row :gutter="20">
                <el-col :span="12">
                  <div class="report-panel">
                    <h4>原始报告内容</h4>
                    <div class="report-content">
                      <p><b>检查名称：</b> 胸部CT平扫</p>
                      <p><b>检查时间：</b> 2024-05-10</p>
                      <p><b>检查所见：</b></p>
                      <p>右肺上叶见一不规则结节影，大小约15mm×12mm，密度不均，边界模糊，毛刺征阳性，可见胸膜凹陷征。双肺散在小结节，直径1-3mm，边界清晰。</p>
                      <p>纵隔内未见明显肿大淋巴结。心影大小正常。</p>
                      <p><b>检查提示：</b></p>
                      <p>1. 右肺上叶结节影，考虑恶性可能，建议进一步检查。</p>
                      <p>2. 双肺散在小结节，考虑良性可能，建议随访观察。</p>
                    </div>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="report-panel ai-analysis">
                    <h4>AI解读结果</h4>
                    <div class="report-content">
                      <div class="risk-level high">
                        <i class="el-icon-warning"></i> 高度关注
                      </div>
                      <p class="analysis-summary">检测到右肺上叶存在高度疑似恶性肿瘤的结节（恶性概率约85%）</p>
                      <el-collapse v-model="activeLung">
                        <el-collapse-item title="关键发现解读" name="1">
                          <ul>
                            <li><b>右肺上叶结节</b>：15mm×12mm的不规则结节，伴有毛刺征和胸膜凹陷征，这些是肺癌的典型影像学特征。</li>
                            <li><b>风险评估</b>：根据结节大小、形态和特征，属于高度疑似恶性病变，建议立即进行进一步检查。</li>
                            <li><b>双肺小结节</b>：1-3mm小结节多为良性病变，如炎症后改变、肉芽肿等，需定期随访。</li>
                          </ul>
                        </el-collapse-item>
                        <el-collapse-item title="建议下一步" name="2">
                          <ul>
                            <li>建议尽快进行PET-CT检查评估全身情况</li>
                            <li>考虑CT引导下肺穿刺活检或纤维支气管镜检查获取病理</li>
                            <li>建议呼吸科和胸外科多学科会诊</li>
                            <li>3-6个月后复查胸部CT评估小结节变化</li>
                          </ul>
                        </el-collapse-item>
                        <el-collapse-item title="健康科普" name="3">
                          <p>肺结节是胸部CT检查中常见的发现，根据大小、形态和特征可评估良恶性可能。</p>
                          <p>结节大于10mm、边缘不规则、有毛刺、胸膜凹陷等特征提示恶性可能性增加。</p>
                          <p>早期肺癌如能及时诊断治疗，5年生存率可达70%以上。</p>
                        </el-collapse-item>
                      </el-collapse>
                    </div>
                  </div>
                </el-col>
              </el-row>
            </el-tab-pane>
            <el-tab-pane label="血液检验报告" name="blood">
              <el-row :gutter="20">
                <el-col :span="12">
                  <div class="report-panel">
                    <h4>原始报告内容</h4>
                    <div class="report-content">
                      <p><b>检查名称：</b> 血常规+肝功能</p>
                      <p><b>检查时间：</b> 2024-05-12</p>
                      <p><b>检查结果：</b></p>
                      <table class="report-table">
                        <tr>
                          <th>检测项目</th>
                          <th>结果</th>
                          <th>参考范围</th>
                        </tr>
                        <tr>
                          <td>白细胞(WBC)</td>
                          <td>6.5×10^9/L</td>
                          <td>4.0-10.0×10^9/L</td>
                        </tr>
                        <tr>
                          <td>红细胞(RBC)</td>
                          <td>4.2×10^12/L</td>
                          <td>3.5-5.5×10^12/L</td>
                        </tr>
                        <tr class="abnormal">
                          <td>血红蛋白(HGB)</td>
                          <td>108 g/L</td>
                          <td>120-160 g/L</td>
                        </tr>
                        <tr class="abnormal">
                          <td>丙氨酸氨基转移酶(ALT)</td>
                          <td>63 U/L</td>
                          <td>≤ 40 U/L</td>
                        </tr>
                        <tr class="abnormal">
                          <td>天门冬氨酸氨基转移酶(AST)</td>
                          <td>58 U/L</td>
                          <td>≤ 40 U/L</td>
                        </tr>
                        <tr>
                          <td>总胆红素(TBIL)</td>
                          <td>15.2 μmol/L</td>
                          <td>≤ 21 μmol/L</td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="report-panel ai-analysis">
                    <h4>AI解读结果</h4>
                    <div class="report-content">
                      <div class="risk-level medium">
                        <i class="el-icon-warning"></i> 中度关注
                      </div>
                      <p class="analysis-summary">检测到轻度贫血及肝功能异常，建议进一步检查</p>
                      <el-collapse v-model="activeBlood">
                        <el-collapse-item title="异常指标解读" name="1">
                          <ul>
                            <li><b>血红蛋白(HGB) 108 g/L</b>：低于正常范围，提示轻度贫血，可能与营养不良、慢性失血或某些疾病相关。</li>
                            <li><b>丙氨酸氨基转移酶(ALT) 63 U/L</b>：轻度升高，提示肝脏功能受损。</li>
                            <li><b>天门冬氨酸氨基转移酶(AST) 58 U/L</b>：轻度升高，常见于肝细胞损伤。</li>
                          </ul>
                        </el-collapse-item>
                        <el-collapse-item title="可能原因" name="2">
                          <ul>
                            <li>轻度病毒性肝炎</li>
                            <li>药物性肝损伤</li>
                            <li>脂肪肝</li>
                            <li>酒精性肝损伤</li>
                            <li>贫血可能与肝功能异常有关，也可能是独立问题</li>
                          </ul>
                        </el-collapse-item>
                        <el-collapse-item title="建议措施" name="3">
                          <ul>
                            <li>完善肝炎病毒标志物检查</li>
                            <li>复查血常规及肝功能（2-4周后）</li>
                            <li>肝脏超声检查排除结构性病变</li>
                            <li>咨询消化内科医师进一步诊治</li>
                            <li>避免饮酒，减少高脂饮食</li>
                            <li>避免使用可能损伤肝脏的药物</li>
                          </ul>
                        </el-collapse-item>
                      </el-collapse>
                    </div>
                  </div>
                </el-col>
              </el-row>
            </el-tab-pane>
          </el-tabs>
        </div>
      </el-card>
  
      <!-- FAQ区域 -->
      <el-card class="faq-card">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-question"></i> 常见问题解答</span>
        </div>
        <el-collapse v-model="activeFaq">
          <el-collapse-item name="1">
            <template slot="title">
              <span class="faq-title">AI能够替代医生的诊断吗？</span>
            </template>
            <div>
              不能。AI报告解读是辅助工具，旨在帮助理解医学报告，提供初步分析和建议，但不能替代专业医生的诊断。AI解读结果应在医生指导下参考使用。最终诊断和治疗方案仍需由专业医疗人员决定。
            </div>
          </el-collapse-item>
          <el-collapse-item name="2">
            <template slot="title">
              <span class="faq-title">系统支持哪些类型的医疗报告解读？</span>
            </template>
            <div>
              目前系统支持以下几类医疗报告的解读：
              <ul>
                <li>影像学报告：CT、MRI、X光等</li>
                <li>实验室检查报告：血常规、生化、肿瘤标志物等</li>
                <li>病理报告：细胞学和组织学检查</li>
                <li>心电图报告</li>
              </ul>
              系统对肺部影像和前列腺相关检查有特别优化，解读准确率更高。
            </div>
          </el-collapse-item>
          <el-collapse-item name="3">
            <template slot="title">
              <span class="faq-title">AI解读需要多长时间？</span>
            </template>
            <div>
              大多数报告解读在30秒内完成。复杂的影像学报告可能需要1-2分钟。系统会实时显示解读进度，请耐心等待。
            </div>
          </el-collapse-item>
          <el-collapse-item name="4">
            <template slot="title">
              <span class="faq-title">我的报告数据安全吗？</span>
            </template>
            <div>
              是的，我们非常重视您的数据安全和隐私保护：
              <ul>
                <li>所有上传的报告数据采用加密传输和存储</li>
                <li>解读完成后，原始报告可选择自动删除</li>
                <li>严格遵守医疗数据保密规定，不会将您的数据用于其他商业用途</li>
                <li>系统定期进行安全审计和漏洞修复</li>
              </ul>
            </div>
          </el-collapse-item>
          <el-collapse-item name="5">
            <template slot="title">
              <span class="faq-title">如何提高AI解读的准确性？</span>
            </template>
            <div>
              为获得更准确的解读结果，建议：
              <ul>
                <li>上传清晰完整的报告原文或图片</li>
                <li>确保报告内容包含完整的检查结果和医生意见</li>
                <li>如果是影像报告，可同时上传相关的影像图片</li>
                <li>填写相关的病史信息，帮助AI更准确理解报告背景</li>
              </ul>
            </div>
          </el-collapse-item>
        </el-collapse>
      </el-card>
  
      <!-- 结果弹窗 -->
      <el-dialog
        title="AI报告解读结果"
        :visible.sync="dialogVisible"
        width="80%"
        :before-close="handleClose">
        <div v-if="analysisResult.status === 'success'" class="result-content">
          <div class="risk-panel">
            <div class="risk-level" :class="analysisResult.risk">
              <i class="el-icon-warning"></i> {{ getRiskText(analysisResult.risk) }}
            </div>
            <p class="analysis-summary">{{ analysisResult.summary }}</p>
          </div>
  
          <el-tabs v-model="resultActiveTab">
            <el-tab-pane label="解读详情" name="detail">
              <el-collapse v-model="resultActiveCollapse" accordion>
                <el-collapse-item title="关键发现解读" name="1">
                  <div v-html="analysisResult.keyFindings"></div>
                </el-collapse-item>
                <el-collapse-item title="诊断建议" name="2">
                  <div v-html="analysisResult.suggestions"></div>
                </el-collapse-item>
                <el-collapse-item title="健康科普" name="3">
                  <div v-html="analysisResult.education"></div>
                </el-collapse-item>
              </el-collapse>
            </el-tab-pane>
            <el-tab-pane label="原始报告" name="origin">
              <div class="origin-report">
                <div v-html="analysisResult.originalReport"></div>
              </div>
            </el-tab-pane>
          </el-tabs>
          
          <div class="action-buttons">
            <el-button type="primary" @click="saveAnalysis">保存解读结果</el-button>
            <el-button type="success" @click="shareToDoctors">分享给医生</el-button>
            <el-button @click="exportPDF">导出PDF</el-button>
          </div>
        </div>
        <div v-else class="analysis-failed">
          <i class="el-icon-warning-outline"></i>
          <p>很抱歉，解读失败。可能的原因：</p>
          <ul>
            <li>上传的文件不是有效的医疗报告</li>
            <li>报告内容不完整或图像不清晰</li>
            <li>当前AI模型不支持该类型的报告解读</li>
          </ul>
          <p>建议尝试重新上传更清晰的报告或选择其他类型的报告。</p>
        </div>
      </el-dialog>
    </div>
  </template>
  
  <script>
  export default {
    name: "AIReportExplain",
    data() {
      return {
        showUpload: false,
        analyzing: false,
        uploadType: "file",
        fileList: [],
        searchKeyword: "",
        demoTab: "lung",
        resultActiveTab: "detail",
        resultActiveCollapse: "1",
        activeLung: ["1"],
        activeBlood: ["1"],
        activeFaq: ["1"],
        dialogVisible: false,
        
        // 模拟已有报告
        recentReports: [
          { date: "2024-05-10", name: "胸部CT", department: "放射科" },
          { date: "2024-04-25", name: "前列腺彩超", department: "超声科" },
          { date: "2024-04-15", name: "血常规+肝功能", department: "检验科" },
          { date: "2024-03-20", name: "肿瘤标志物检测", department: "检验科" },
        ],
        
        // 模拟解析结果
        analysisResult: {
          status: "success", // success, failed
          risk: "high", // high, medium, low
          summary: "检测到右肺上叶存在高度疑似恶性肿瘤的结节（恶性概率约85%）",
          keyFindings: "<ul><li><b>右肺上叶结节</b>：15mm×12mm的不规则结节，伴有毛刺征和胸膜凹陷征，这些是肺癌的典型影像学特征。</li><li><b>风险评估</b>：根据结节大小、形态和特征，属于高度疑似恶性病变，建议立即进行进一步检查。</li><li><b>双肺小结节</b>：1-3mm小结节多为良性病变，如炎症后改变、肉芽肿等，需定期随访。</li></ul>",
          suggestions: "<ul><li>建议尽快进行PET-CT检查评估全身情况</li><li>考虑CT引导下肺穿刺活检或纤维支气管镜检查获取病理</li><li>建议呼吸科和胸外科多学科会诊</li><li>3-6个月后复查胸部CT评估小结节变化</li></ul>",
          education: "<p>肺结节是胸部CT检查中常见的发现，根据大小、形态和特征可评估良恶性可能。</p><p>结节大于10mm、边缘不规则、有毛刺、胸膜凹陷等特征提示恶性可能性增加。</p><p>早期肺癌如能及时诊断治疗，5年生存率可达70%以上。</p>",
          originalReport: "<p><b>检查名称：</b> 胸部CT平扫</p><p><b>检查时间：</b> 2024-05-10</p><p><b>检查所见：</b></p><p>右肺上叶见一不规则结节影，大小约15mm×12mm，密度不均，边界模糊，毛刺征阳性，可见胸膜凹陷征。双肺散在小结节，直径1-3mm，边界清晰。</p><p>纵隔内未见明显肿大淋巴结。心影大小正常。</p><p><b>检查提示：</b></p><p>1. 右肺上叶结节影，考虑恶性可能，建议进一步检查。</p><p>2. 双肺散在小结节，考虑良性可能，建议随访观察。</p>"
        },
  
        // 功能介绍
        features: [
          {
            icon: "el-icon-reading",
            title: "智能解析",
            description: "采用最新深度学习算法，精准识别报告中的关键信息和异常指标"
          },
          {
            icon: "el-icon-time",
            title: "快速高效",
            description: "30秒内完成解读，节省阅读和理解专业术语的时间"
          },
          {
            icon: "el-icon-data-analysis",
            title: "多维分析",
            description: "全面分析疾病风险，提供多角度诊断建议"
          },
          {
            icon: "el-icon-first-aid-kit",
            title: "专业可靠",
            description: "基于100万+临床案例训练，专注肺癌、前列腺癌等重点疾病"
          }
        ]
      }
    },
    methods: {
      startAnalysis() {
        this.showUpload = true;
      },
  
      handleFileChange(file) {
        this.fileList = [file];
      },
  
      selectReport(report) {
        this.$message.success(`已选择: ${report.name}`);
        this.selectedReport = report;
      },
  
      analyzeReport() {
        if (this.uploadType === "file" && this.fileList.length === 0) {
          this.$message.warning("请先上传报告文件");
          return;
        }
  
        if (this.uploadType === "record" && !this.selectedReport) {
          this.$message.warning("请先选择一份报告");
          return;
        }
  
        this.analyzing = true;
        // 模拟分析过程
        setTimeout(() => {
          this.analyzing = false;
          this.dialogVisible = true;
          this.showUpload = false;
        }, 2000);
      },
  
      resetUpload() {
        this.fileList = [];
        this.selectedReport = null;
      },
  
      handleClose() {
        this.dialogVisible = false;
      },
  
      saveAnalysis() {
        this.$message.success("解读结果已保存到您的健康档案");
      },
  
      shareToDoctors() {
        this.$message.success("解读结果已成功分享给您的主治医生");
      },
  
      exportPDF() {
        this.$message.success("报告解读结果已导出为PDF文件");
      },
  
      getRiskText(risk) {
        const riskMap = {
          high: "高度关注",
          medium: "中度关注",
          low: "一般关注"
        };
        return riskMap[risk] || "一般关注";
      }
    }
  }
  </script>
  
  <style lang="scss" scoped>
  .header-banner {
    height: 220px;
    width: 100%;
    border-radius: 8px;
    margin-bottom: 20px;
    background: linear-gradient(135deg, #409EFF 0%, #1976D2 100%);
    position: relative;
    overflow: hidden;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="none"/><path d="M30,20 L70,20 C75.5,20 80,24.5 80,30 L80,70 C80,75.5 75.5,80 70,80 L30,80 C24.5,80 20,75.5 20,70 L20,30 C20,24.5 24.5,20 30,20 Z" stroke="rgba(255,255,255,0.1)" stroke-width="2" fill="none"/></svg>') repeat;
      opacity: 0.3;
    }
  
    .banner-content {
      text-align: center;
      z-index: 1;
      padding: 0 20px;
  
      h1 {
        font-size: 28px;
        font-weight: 600;
        margin-bottom: 10px;
      }
  
      p {
        font-size: 16px;
        margin-bottom: 20px;
        max-width: 600px;
      }
    }
  }
  
  .feature-card {
    margin-bottom: 20px;
  
    .feature-content {
      padding: 10px;
    }
  
    .feature-item {
      text-align: center;
      padding: 20px;
      height: 100%;
      border-radius: 4px;
      transition: all 0.3s;
  
      &:hover {
        background-color: #f5f7fa;
        transform: translateY(-5px);
      }
  
      .feature-icon {
        font-size: 36px;
        color: #409EFF;
        margin-bottom: 15px;
      }
  
      h3 {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 10px;
      }
  
      p {
        color: #606266;
        font-size: 14px;
      }
    }
  }
  
  .upload-card {
    margin-bottom: 20px;
  
    .upload-area {
      padding: 10px;
    }
  
    .camera-container, .record-container {
      text-align: center;
      padding: 30px 20px;
  
      i {
        font-size: 48px;
        color: #909399;
        margin-bottom: 15px;
        display: block;
      }
  
      p {
        margin-bottom: 15px;
      }
    }
  
    .upload-action {
      text-align: center;
      margin-top: 20px;
    }
  }
  
  .demo-card {
    margin-bottom: 20px;
  
    .report-panel {
      padding: 15px;
      border: 1px solid #EBEEF5;
      border-radius: 4px;
      height: 100%;
      min-height: 400px;
      position: relative;
  
      h4 {
        margin-top: 0;
        padding-bottom: 10px;
        border-bottom: 1px solid #EBEEF5;
        font-size: 16px;
      }
  
      .report-content {
        margin-top: 15px;
      }
  
      &.ai-analysis {
        background-color: #f8f9fa;
        border-color: #e9ecef;
      }
    }
  
    .report-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
  
      th, td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      }
  
      th {
        background-color: #f2f2f2;
      }
  
      .abnormal {
        color: #F56C6C;
        font-weight: bold;
      }
    }
  
    .risk-level {
      padding: 8px 12px;
      border-radius: 4px;
      display: inline-block;
      margin-bottom: 15px;
      font-weight: bold;
  
      &.high {
        background-color: rgba(245, 108, 108, 0.1);
        color: #F56C6C;
      }
  
      &.medium {
        background-color: rgba(230, 162, 60, 0.1);
        color: #E6A23C;
      }
  
      &.low {
        background-color: rgba(103, 194, 58, 0.1);
        color: #67C23A;
      }
    }
  
    .analysis-summary {
      font-size: 16px;
      margin-bottom: 15px;
      font-weight: 500;
    }
  }
  
  .faq-card {
    margin-bottom: 20px;
  
    .faq-title {
      font-weight: bold;
      color: #303133;
    }
  }
  
  .result-content {
    .risk-panel {
      margin-bottom: 20px;
    }
  
    .action-buttons {
      margin-top: 20px;
      text-align: center;
    }
  
    .origin-report {
      background-color: #f8f9fa;
      padding: 15px;
      border-radius: 4px;
      border: 1px solid #e9ecef;
    }
  }
  
  .analysis-failed {
    text-align: center;
    padding: 30px;
  
    i {
      font-size: 48px;
      color: #E6A23C;
      margin-bottom: 15px;
    }
  
    p {
      font-size: 16px;
      margin-bottom: 15px;
    }
  
    ul {
      display: inline-block;
      text-align: left;
      margin-bottom: 15px;
    }
  }
  </style>